import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { Calendar, CheckCircle, Clock, ShieldCheck, User } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import { SearchInput } from "@/components/shared/SearchInput";
import { VerificationBadge } from "@/components/shared/VerificationBadge";
import { Field } from "@/components/forms";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { formatDate } from "@/utils/formatters";
import { usePartnerWorkLogs } from "@/hooks/usePartnerData";
import { useReviewWorkLog } from "@/hooks/useWorkLogs";

export const Route = createFileRoute("/partner/verification/")({
  component: PartnerVerificationPage,
});

function normalizeRecord(log: any) {
  return {
    id: log.id,
    student_name: log.student_name || log.studentName || `Student #${log.student_id || ""}`,
    placement_title: log.placement_title || log.placementTitle || `Placement #${log.placement_id || ""}`,
    date: log.date || log.created_at,
    total_hours: Number(log.hours || 0),
    status: String(log.status || "pending").toLowerCase(),
    description: log.description || log.content || "",
    feedback: log.feedback || "",
    reviewed_at: log.reviewed_at,
  };
}

function PartnerVerificationPage() {
  const [search, setSearch] = useState("");
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [feedback, setFeedback] = useState("");

  const { data: rawLogs = [], isLoading } = usePartnerWorkLogs();
  const reviewMutation = useReviewWorkLog();

  const records = useMemo(() => (Array.isArray(rawLogs) ? rawLogs.map(normalizeRecord) : []), [rawLogs]);
  const pendingRecords = records.filter((r) => r.status === "pending" || r.status === "changes_requested");
  const verifiedRecords = records.filter((r) => r.status === "approved");
  const filteredPending = pendingRecords.filter(
    (r) =>
      !search ||
      r.student_name.toLowerCase().includes(search.toLowerCase()) ||
      r.placement_title.toLowerCase().includes(search.toLowerCase()),
  );

  const handleConfirmVerify = async () => {
    if (!selectedRecord) return;
    try {
      await reviewMutation.mutateAsync({
        id: String(selectedRecord.id),
        status: "approved",
        feedback: feedback.trim() || "Verified by partner supervisor.",
      });
      toast.success("Work log verified", {
        description: `${selectedRecord.student_name}'s work log has been approved.`,
      });
      setSelectedRecord(null);
      setFeedback("");
    } catch {
      toast.error("Verification failed. Please try again.");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Verify work experience"
        subtitle="Review and approve real work logs submitted by students assigned to your organization."
        breadcrumbs={[{ label: "Dashboard", path: "/partner/dashboard" }, { label: "Verification" }]}
      />

      <Alert>
        <ShieldCheck />
        <div>
          <AlertTitle>Partner verification authority</AlertTitle>
          <AlertDescription className="text-muted-foreground">
            Approval writes back to the work-log review API and becomes part of the student record.
          </AlertDescription>
        </div>
      </Alert>

      <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
        <StatCard label="Pending review" value={pendingRecords.length} icon={<Clock />} />
        <StatCard label="Verified logs" value={verifiedRecords.length} icon={<CheckCircle />} />
        <StatCard
          label="Students represented"
          value={new Set(records.map((r) => r.student_name)).size}
          icon={<User />}
        />
        <StatCard
          label="Hours reviewed"
          value={verifiedRecords.reduce((s, r) => s + r.total_hours, 0)}
          icon={<ShieldCheck />}
        />
      </div>

      {isLoading ? (
        <TableSkeleton rows={5} columns={4} />
      ) : (
        <>
          <SectionCard
            title="Pending verification"
            noPadding
            actions={
              <SearchInput
                placeholder="Search by student or placement..."
                value={search}
                onChange={setSearch}
                className="sm:w-72"
              />
            }
          >
            {filteredPending.length === 0 ? (
              <EmptyState
                bare
                icon={<ShieldCheck />}
                title="Nothing to verify"
                description="No pending work logs from the API."
              />
            ) : (
              <ul className="divide-y divide-border">
                {filteredPending.map((record) => (
                  <li
                    key={record.id}
                    className="flex flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-5"
                  >
                    <div className="min-w-0">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-foreground">
                          {record.student_name}
                        </span>
                        <VerificationBadge status="pending" />
                      </div>
                      <p className="text-sm text-body">{record.placement_title}</p>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-caption text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="size-3" aria-hidden /> {formatDate(record.date)}
                        </span>
                        <span className="inline-flex items-center gap-1 tabular-nums">
                          <Clock className="size-3" aria-hidden /> {record.total_hours} hrs
                        </span>
                      </div>
                      {record.description && (
                        <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
                          {record.description}
                        </p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedRecord(record)}
                      className="shrink-0 max-md:self-start"
                    >
                      <CheckCircle />
                      Verify log
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          <SectionCard title="Verified history" noPadding>
            {verifiedRecords.length === 0 ? (
              <p className="px-4 py-6 text-sm text-muted-foreground md:px-5">
                No approved work logs yet.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {verifiedRecords.map((record) => (
                  <li key={record.id} className="px-4 py-3 md:px-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-foreground">
                        {record.student_name}
                      </span>
                      <VerificationBadge status="verified" />
                      <Badge variant="outline" className="tabular-nums">
                        {record.total_hours} hrs
                      </Badge>
                      {record.reviewed_at && (
                        <Badge variant="muted">Reviewed {formatDate(record.reviewed_at)}</Badge>
                      )}
                    </div>
                    <p className="mt-0.5 text-caption text-muted-foreground">
                      {record.placement_title}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </>
      )}

      <Dialog open={Boolean(selectedRecord)} onOpenChange={(o) => !o && setSelectedRecord(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Verify work log</DialogTitle>
            <DialogDescription>
              This will approve the selected work log through the partner review endpoint.
            </DialogDescription>
          </DialogHeader>
          <Field label="Feedback">
            <Textarea
              rows={3}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Optional review note for the student"
            />
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedRecord(null)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmVerify} loading={reviewMutation.isPending}>
              {reviewMutation.isPending ? "Verifying..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
